import { useEffect, useRef } from "react";
import { Check, Clock3, ClockAlert } from "lucide-react";
import { PICKUP_ACK_LABELS, pickupAckStep } from "../../core/pickup";
import type { PickupAckStep, PickupPunctuality, PickupState } from "../../shared/types";
import "./PickupCommunication.css";

function MessageTime({ atMs }: { atMs: number }) {
	return <time dateTime={new Date(atMs).toISOString()}>{new Date(atMs).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</time>;
}

export function PickupMessages({ pickup, saving = false, onShown }: { pickup: PickupState; saving?: boolean; onShown?: () => void }) {
	const host = useRef<HTMLDivElement>(null);
	const communication = pickup.communication;
	const active = pickup.status === "active" || pickup.status === "offered";
	const hasMessage = active && !!(communication?.ack || communication?.punctuality);
	useEffect(() => {
		if (!hasMessage || pickup.role !== "rider" || !onShown || !host.current || !communication || communication.shownRevision >= communication.revision) return;
		let inView = false;
		let sent = false;
		let frame = 0;
		const show = () => {
			if (inView && document.visibilityState === "visible" && !sent) {
				frame = requestAnimationFrame(() => {
					if (inView && document.visibilityState === "visible" && !sent) { sent = true; onShown(); }
				});
			}
		};
		const observer = new IntersectionObserver(entries => { inView = entries[0].intersectionRatio >= 1; show(); }, { threshold: 1 });
		observer.observe(host.current);
		document.addEventListener("visibilitychange", show);
		return () => { observer.disconnect(); cancelAnimationFrame(frame); document.removeEventListener("visibilitychange", show); };
	}, [hasMessage, pickup.role, communication, onShown]);
	if (!hasMessage || !communication) return null;
	const ack = communication.ack;
	const punctuality = communication.punctuality;
	const current = ack?.step === pickupAckStep(pickup);
	return <div ref={host} className="pickup-messages" role="status">
		{ack && <p><Check size={17} aria-hidden="true" /><span>{pickup.role === "driver"
			? `${current ? "Ack'd" : "Previously ack'd"} ${PICKUP_ACK_LABELS[ack.step].driver}`
			: current ? PICKUP_ACK_LABELS[ack.step].rider : `Your ride acknowledged ${PICKUP_ACK_LABELS[ack.step].driver}`}<MessageTime atMs={ack.atMs} /></span></p>}
		{punctuality && <p data-punctuality={punctuality.status}><Clock3 size={17} aria-hidden="true" /><span>{punctuality.status === "on_time" ? "On time" : "Running late"}<MessageTime atMs={punctuality.atMs} /></span></p>}
		{pickup.role === "driver" && <small>{saving ? "Sending…" : communication.shownRevision >= communication.revision ? "Seen on rider's screen" : "Sent. Rider hasn't seen it yet."}</small>}
	</div>;
}

export function PickupCommunication({ pickup, saving, error, onAck, onPunctuality }: {
	pickup: PickupState;
	saving: boolean;
	error: string | null;
	onAck: (step: PickupAckStep) => void;
	onPunctuality: (status: PickupPunctuality) => void;
}) {
	if (pickup.role !== "driver" || !["active", "offered"].includes(pickup.status)) return null;
	const step = pickupAckStep(pickup);
	const punctuality = pickup.communication?.punctuality?.status;
	return <section className="pickup-communication" aria-label="Pickup messages">
		<div className="pickup-communication__ack">
			<span>{step ? PICKUP_ACK_LABELS[step].driver : "Waiting for rider progress"}</span>
			<button type="button" disabled={saving || !step} aria-busy={saving} title={step ? `Acknowledge ${PICKUP_ACK_LABELS[step].driver}` : "Waiting for rider progress"} onClick={() => { if (step) onAck(step); }}><Check size={18} aria-hidden="true" />Ack</button>
		</div>
		<PickupMessages pickup={pickup} saving={saving} />
		<div className="pickup-communication__timing" role="group" aria-label="Driver timing">
			<button type="button" disabled={saving} aria-pressed={punctuality === "on_time"} onClick={() => onPunctuality("on_time")}><Clock3 size={17} aria-hidden="true" />On Time</button>
			<button type="button" disabled={saving} aria-pressed={punctuality === "running_late"} onClick={() => onPunctuality("running_late")}><ClockAlert size={17} aria-hidden="true" />Running Late</button>
		</div>
		{error && <p role="alert">{error}</p>}
	</section>;
}